/**
 * Static option lists and formatting helpers for the dashboard filter rail.
 * Kept out of the components so labels stay in one place.
 */
import { SOURCE_META } from '../../SourceLogo/SourceLogo';

/** Short labels for resource categories (table cells + Source filter). */
export const CATEGORY_LABELS = {
    video: 'Video',
    pinComment: 'Pin comment',
    qr: 'QR code',
    email: 'Email',
    community: 'Community',
    channel: 'Channel',
    twitter: 'Twitter',
    instagram: 'Instagram',
    threads: 'Threads',
    linkedin: 'LinkedIn',
    podcast: 'Podcast',
    blog: 'Blog',
    tiktok: 'TikTok',
    ad: 'Ad',
    website: 'Website',
    telegram: 'Telegram',
    facebook: 'Facebook',
    discord: 'Discord',
    live: 'Live stream',
    webinar: 'Webinar',
    leadMagnet: 'Lead magnet',
};

export const getCategoryLabel = (category) => CATEGORY_LABELS[category] ?? category ?? '';

/** Icon key (see SOURCE_ICONS) for a resource category. */
export const getCategoryIconKey = (category) => SOURCE_META[category]?.icon ?? 'website';

export const SOURCE_OPTIONS = [
    { value: 'all', label: 'All sources' },
    ...Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
        value,
        label,
        iconKey: getCategoryIconKey(value),
    })),
];

export const METRIC_COLUMN_OPTIONS = [
    { value: 'views', label: 'Views' },
    { value: 'engagedViews', label: 'Engaged views' },
    { value: 'clicks', label: 'Clicks' },
    { value: 'sales', label: 'Sales' },
    { value: 'emails', label: 'Emails' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'callBookings', label: 'Call bookings' },
    { value: 'clickPercentage', label: 'Click %' },
    { value: 'salesPercentage', label: 'Sales %' },
    { value: 'emailsFromViewsPercentage', label: 'Emails % from views' },
    { value: 'first_event', label: 'First event' },
];

export const METRIC_OPERATOR_OPTIONS = [
    { value: 'above', label: 'is above', symbol: '>' },
    { value: 'below', label: 'is below', symbol: '<' },
];

export const COMBINE_DATA_OPTIONS = [
    { value: 'no', label: 'No', description: 'Show videos, QR codes and pinned comments as separate rows.' },
    { value: 'yes', label: 'Yes', description: 'Merge pinned comment and QR data into each video. Click a video row to see the breakdown.' },
];

export const EMPTY_METRIC_FILTER = { column: '', operator: 'above', value: '' };

export const findOptionLabel = (options, value) => options.find((option) => option.value === value)?.label ?? '';

const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const daysAgo = (days) => {
    const d = startOfDay(new Date());
    d.setDate(d.getDate() - days);
    return d;
};

export const DATE_RANGE_PRESETS = [
    { key: 'last7', label: 'Last 7 days', getRange: () => [daysAgo(6), startOfDay(new Date())] },
    { key: 'last30', label: 'Last 30 days', getRange: () => [daysAgo(29), startOfDay(new Date())] },
    {
        key: 'thisMonth',
        label: 'This month',
        getRange: () => {
            const now = new Date();
            return [new Date(now.getFullYear(), now.getMonth(), 1), startOfDay(now)];
        },
    },
];

const shortDate = (date, withYear) => date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
});

/**
 * "Oct 1 – Oct 31" for a complete range, "Oct 1 – …" while only the start is
 * picked. The year is appended when the range leaves the current year.
 */
export const formatDateRange = (start, end) => {
    if (!start && !end) return '';
    const currentYear = new Date().getFullYear();
    const needsYear = [start, end].some((d) => d && d.getFullYear() !== currentYear);
    const from = start ? shortDate(start, needsYear) : '…';
    const to = end ? shortDate(end, needsYear) : '…';
    return `${from} – ${to}`;
};
